import React from "react"
import { Box, Typography, useTheme } from "@mui/material"
import AppChip from "@/components/ui/AppChip"
import { useOrderNotifications } from "@/hooks/use-order-notifications"

export type OrderNotificationsOptInProps = {
  restaurantId: string
  /** False when the order is in a terminal state and can no longer notify. */
  eligible: boolean
}

/**
 * One unobtrusive opt-in row on Order Details.
 *
 * Renders nothing at all when the browser cannot do Web Push, when the order can no longer
 * produce a notification, or when the customer has blocked notifications — the customer is
 * never re-prompted, and there is no settings screen, preference or notification centre.
 */
export function OrderNotificationsOptIn({ restaurantId, eligible }: OrderNotificationsOptInProps) {
  const theme = useTheme()
  const { visible, enabled, showAction, busy, message, enable } = useOrderNotifications({
    restaurantId,
    eligible,
  })

  if (!visible) return null

  return (
    <Box
      data-testid="order-notifications-opt-in"
      sx={{
        px: 0,
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: { xs: "flex-start", sm: "center" },
        justifyContent: "space-between",
        gap: theme.spacing(0.75),
        pb: theme.spacing(0.5),
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
        {enabled
          ? "Order notifications enabled"
          : message ?? "Get notified when this order is accepted, ready, or out for delivery."}
      </Typography>

      {showAction ? (
        <AppChip
          size="small"
          label={busy ? "Enabling…" : "Enable order updates"}
          disabled={busy}
          onClick={enable}
          sx={{ fontWeight: theme.typography.fontWeightBold, flexShrink: 0 }}
          aria-label="Enable order updates"
          data-testid="order-notifications-enable"
        />
      ) : null}
    </Box>
  )
}

export default OrderNotificationsOptIn
